import { Chip } from '@/components/Chip/Chip';
import type { MessageTemplate } from '@/graphql/message-templates';
import { formatDate, shortId } from '@/lib/format';

import styles from './TemplateCard.module.css';

export interface TemplateCardProps {
  template: MessageTemplate;
  onOpen: (id: string) => void;
  /** Deleted in this session. The row stays until the listing is refetched. */
  deleted?: boolean;
  /** Move this row one place in its own list. Absent when there is nowhere for
   *  it to go — the ends of the list, and a row the filters have left with no
   *  visible neighbour on that side. */
  onMove?: (direction: 'up' | 'down') => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  /** A reorder of this row's list is in flight. Every row in the list is being
   *  written, so they all stop taking clicks together. */
  reordering?: boolean;
}

/**
 * One template, as a row in the listing.
 *
 * The body is previewed rather than hidden, at one clamped line: it comes back
 * verbatim with its placeholders intact, and `{{ applicant.firstName }}` in a
 * list is what tells a reader which of four chase-up templates this is when
 * their titles do not.
 *
 * The move buttons sit **beside** the open button rather than inside it,
 * because a button inside a button is not a thing a browser will render — and
 * the whole row being the way in to the modal is what the listing is for. Each
 * one names the template it moves: eight buttons reading "Up" are eight
 * identical buttons to anyone not looking at the row.
 */
export function TemplateCard({
  template,
  onOpen,
  deleted = false,
  onMove,
  canMoveUp = false,
  canMoveDown = false,
  reordering = false,
}: TemplateCardProps) {
  const version = template.currentVersion;
  const brokerage = template.tenantId !== null;
  const draft = template.status === 'DRAFT';
  const title = version?.title ?? '(no current version)';

  return (
    <li className={`${styles.item} ${deleted ? styles.deleted : ''}`}>
      {onMove ? (
        <span aria-label={`Order of ${title}`} className={styles.rank} role="group">
          <button
            aria-label={`Move ${title} up`}
            className={styles.move}
            disabled={!canMoveUp || reordering || deleted}
            onClick={() => onMove('up')}
            type="button"
          >
            ↑
          </button>
          <span className={styles.position}>{template.position ?? '—'}</span>
          <button
            aria-label={`Move ${title} down`}
            className={styles.move}
            disabled={!canMoveDown || reordering || deleted}
            onClick={() => onMove('down')}
            type="button"
          >
            ↓
          </button>
        </span>
      ) : null}

      <button className={styles.open} onClick={() => onOpen(template.id)} type="button">
        <span className={styles.head}>
          <span className={styles.title}>{title}</span>
          <span className={styles.updated}>{formatDate(template.updatedAt)}</span>
        </span>

        <span className={styles.body}>{version?.body ?? ''}</span>

        <span className={styles.chips}>
          {deleted ? <Chip tone="bad">deleted</Chip> : null}
          <Chip tone={brokerage ? 'good' : 'neutral'}>{brokerage ? 'Brokerage' : 'Personal'}</Chip>
          <Chip tone={draft ? 'warn' : 'neutral'}>{draft ? 'draft' : 'active'}</Chip>
          <Chip>{template.category ?? 'ungrouped'}</Chip>
          <Chip label="v">{version?.versionNumber ?? '—'}</Chip>
          <Chip label="versions">{template.versionCount}</Chip>
          {template.duplicatedFromMessageTemplateId ? <Chip>forked</Chip> : null}
          <span className={styles.id}>{shortId(template.id)}</span>
        </span>
      </button>
    </li>
  );
}
